/**
 * Task 077 — starter templates for the agency expert / prompt
 * library (`AgencyKnowledgeLibraryPage`).
 *
 * These are FORM-FILL templates only. Selecting one populates the
 * editor fields; nothing reaches the API until the operator clicks
 * the existing create/save button in the modal. The text is
 * deliberately generic so leadership can edit it before approval —
 * the goal is to remove the empty-page friction, not to seed
 * production knowledge.
 *
 * Why a separate file:
 *   - Keeps the page component below ~700 lines.
 *   - Lets a future task ship more templates without touching the
 *     editor wiring.
 *
 * No backend, no AI calls, no inserts.
 */

import type { AiPromptBlockScope, ContentVariantPlatform } from '../api/types';

export interface PromptBlockTemplate {
  /** Stable id for selection state. Not sent to the API. */
  readonly id: string;
  /** Arabic label shown in the picker. */
  readonly label: string;
  /** One-line description shown under the label. */
  readonly description: string;
  /** Form-fill payload. Editor will overlay these onto its state. */
  readonly fill: {
    readonly scope: AiPromptBlockScope;
    readonly title: string;
    readonly body: string;
    readonly applies_to: ReadonlyArray<string>;
    readonly platforms: ReadonlyArray<ContentVariantPlatform>;
    readonly industry: string | null;
    readonly service_key: string | null;
    readonly priority: number;
  };
}

// Common applies_to keys used by the V1 content-calendar workers.
const A_UNIFIED = 'content.calendar_generate.unified';
const A_PLATFORM = 'content.calendar_generate.platform_adapted';

export const PROMPT_BLOCK_TEMPLATES: ReadonlyArray<PromptBlockTemplate> = [
  {
    id: 'client-voice-tone',
    label: '١. صوت ونبرة العميل',
    description: 'كيف يتكلّم البراند: الشخصية، النبرة، اللهجة، مستوى الرسمية.',
    fill: {
      scope: 'client',
      title: 'صوت ونبرة العميل',
      body: [
        'هذا العميل يتحدث بنبرة [مهنية / قريبة من المستخدم / حماسية].',
        'يستخدم العربية الفصيحة الحديثة مع كلمات شائعة بسيطة.',
        'يتجنب الكلمات الإنجليزية إلا للأسماء التقنية (مثل: SEO).',
        '',
        'الشخصية:',
        '- خبير في مجاله بدون تعالي.',
        '- يجاوب باختصار ووضوح.',
        '- لا يستخدم كلمات مبالَغ فيها مثل: ثوري، أفضل، مذهل.',
        '',
        'كل مخرجات الذكاء الاصطناعي يجب أن تطابق هذه النبرة.',
      ].join('\n'),
      applies_to: [A_UNIFIED, A_PLATFORM],
      platforms: [],
      industry: null,
      service_key: null,
      priority: 100,
    },
  },
  {
    id: 'saudi-arabic-caption-style',
    label: '٢. أسلوب التسميات بالعربية السعودية',
    description: 'قواعد كتابة الكابشن للمحتوى السعودي: لهجة، أمثلة، أرقام.',
    fill: {
      scope: 'agency',
      title: 'أسلوب التسميات بالعربية السعودية',
      body: [
        'كل كابشن:',
        '- يبدأ بهوك (سؤال أو إحصائية) في ٨–١٢ كلمة.',
        '- يستخدم العربية الفصيحة الحديثة، مع كلمة أو اثنتين باللهجة الخليجية للقرب.',
        '- يحتوي على دعوة لإجراء واضحة في النهاية (CTA).',
        '- لا يتجاوز ٤ أسطر للجمهور السعودي.',
        '',
        'الأرقام بالعربية الشرقية: ٠ ١ ٢ ٣ ٤ ٥ ٦ ٧ ٨ ٩ — ليست بالغربية.',
        'العملة: ر.س (ليس SAR).',
        'يُمنع استخدام الترميز الإنجليزي مثل #amazing.',
      ].join('\n'),
      applies_to: [A_UNIFIED, A_PLATFORM],
      platforms: [],
      industry: null,
      service_key: null,
      priority: 90,
    },
  },
  {
    id: 'forbidden-phrases',
    label: '٣. كلمات وعبارات ممنوعة',
    description: 'قائمة الكلمات والعبارات التي يجب على الذكاء الاصطناعي تجنبها.',
    fill: {
      scope: 'agency',
      title: 'كلمات وعبارات ممنوعة',
      body: [
        'لا تستخدم العبارات التالية في أي مخرجات:',
        '',
        '- "ثوري"، "أفضل في العالم"، "رقم ١".',
        '- ادعاءات غير مثبتة عن الأداء.',
        '- وعود مالية مضمونة (ROI محدد).',
        '- مقارنات مباشرة بمنافسين بالاسم.',
        '- العبارات الترويجية العامة: "اكتشف الآن"، "لا تفوّت".',
        '',
        'إذا احتاج النص دعوة لإجراء، استخدم صيغة محددة بدل عامة.',
      ].join('\n'),
      applies_to: [A_UNIFIED, A_PLATFORM],
      platforms: [],
      industry: null,
      service_key: null,
      priority: 200,
    },
  },
  {
    id: 'instagram-hooks',
    label: '٤. هوكات Instagram',
    description: 'صيغ افتتاح المنشورات لجذب الانتباه في أول ٣ ثوانٍ.',
    fill: {
      scope: 'platform',
      title: 'هوكات Instagram',
      body: [
        'افتح كل كابشن على Instagram بأحد هذه الأنماط:',
        '',
        '١. سؤال مباشر: "هل تعلم أن…؟"',
        '٢. رقم/إحصائية: "٧٢٪ من العملاء…"',
        '٣. عبارة فضولية: "هذا أهم خطأ يفعله…"',
        '٤. إعلان قيمة: "في ٦٠ ثانية ستعرف كيف…"',
        '٥. تحدّ: "لو كنت مكاني ماذا تختار؟"',
        '',
        'الهوك دائماً قبل أي مقدمة عن العميل أو الخدمة.',
        'لا تكرر الهوك في الكاروسيل: كل سلايد مختلف.',
      ].join('\n'),
      applies_to: [A_PLATFORM],
      platforms: ['instagram_feed', 'instagram_story', 'instagram_reel'],
      industry: null,
      service_key: null,
      priority: 80,
    },
  },
  {
    id: 'short-video-rules',
    label: '٥. قواعد الفيديو القصير (Reels / TikTok)',
    description: 'بنية، طول، وتوقيت الفيديوهات القصيرة.',
    fill: {
      scope: 'platform',
      title: 'قواعد الفيديو القصير',
      body: [
        'كل سكربت فيديو قصير (Reels / TikTok):',
        '',
        '- مدته ١٥–٣٠ ثانية.',
        '- الهوك في أول ٣ ثوانٍ.',
        '- نص على الشاشة في كل لقطة.',
        '- الموسيقى تتبع التريند الحالي على المنصة.',
        '- نهاية واضحة بدعوة لإجراء (تابعنا، احفظ، شارك).',
        '',
        'الكابشن المرفق:',
        '- ٢ سطر فقط.',
        '- يكرّر الفائدة الرئيسية للفيديو.',
        '- ٣–٥ هاشتاقات سعودية فقط.',
      ].join('\n'),
      applies_to: [A_PLATFORM],
      platforms: ['instagram_reel', 'tiktok_video'],
      industry: null,
      service_key: null,
      priority: 80,
    },
  },
  {
    id: 'industry-expertise',
    label: '٦. معرفة قطاعية متخصصة',
    description: 'ضع المصطلحات والأرقام والقواعد الخاصة بقطاع العميل.',
    fill: {
      scope: 'industry',
      title: 'معرفة قطاعية',
      body: [
        'سياق القطاع:',
        '- الفئة المستهدفة: [حدّد العمر، الموقع، السلوك الشرائي].',
        '- ذروة الموسم: [الأشهر].',
        '- المصطلحات المهمة: [اذكر الكلمات المتخصصة وما تعنيه].',
        '- الأرقام المرجعية: [حصة السوق، متوسط السعر، إلخ].',
        '',
        'القواعد الخاصة بالقطاع:',
        '- [مثلاً: الإعلانات الصحية يجب ألا تَعِد بنتائج]',
        '- [مثلاً: المطاعم يجب أن تذكر الالتزامات الغذائية]',
        '',
        'اذكر القطاع بدقة في حقل "القطاع" أعلاه.',
      ].join('\n'),
      applies_to: [A_UNIFIED, A_PLATFORM],
      platforms: [],
      industry: '',
      service_key: null,
      priority: 70,
    },
  },
  {
    id: 'service-cta-rules',
    label: '٧. قواعد العروض ودعوات الإجراء (Service)',
    description: 'كيف تتم صياغة العروض والـ CTA لخدمة معينة.',
    fill: {
      scope: 'service',
      title: 'قواعد العروض ودعوات الإجراء',
      body: [
        'لكل عرض ضمن هذه الخدمة:',
        '',
        '- اكتب العرض بصيغة "ماذا يحصل عليه العميل" لا "ماذا نقدّم".',
        '- حدّد المدة الزمنية إن وُجدت (ينتهي يوم …).',
        '- اذكر شرطًا واحدًا واضحًا (مثلاً: للحجوزات الجديدة فقط).',
        '- استخدم CTA محدد: "احجز الآن"، "اطلب التجربة"، "تواصل واتساب".',
        '- لا تَعِد بأرقام لا يمكن إثباتها.',
        '',
        'املأ "مفتاح الخدمة" أعلاه بالقيمة المتفق عليها (مثل: paid_ads_meta).',
      ].join('\n'),
      applies_to: [A_UNIFIED, A_PLATFORM],
      platforms: [],
      industry: null,
      service_key: '',
      priority: 70,
    },
  },
  {
    id: 'monthly-content-calendar-rules',
    label: '٨. قواعد جدول المحتوى الشهري',
    description: 'تنويع، تكرار، وتوزيع المحتوى عبر الشهر.',
    fill: {
      scope: 'agency',
      title: 'قواعد جدول المحتوى الشهري',
      body: [
        'لكل عميل، يجب أن يحتوي الجدول الشهري على:',
        '',
        '- ٣٠٪ محتوى تعليمي / فائدة.',
        '- ٢٠٪ محتوى ترفيهي / إنساني.',
        '- ٢٠٪ محتوى تجاري / عرض.',
        '- ١٥٪ محتوى مجتمعي / قصص عملاء.',
        '- ١٥٪ محتوى تفاعلي / استبيانات وأسئلة.',
        '',
        'لا يكرّر نفس النوع لأكثر من يومين متتاليين.',
        'الإثنين والخميس: محتوى ترويجي خفيف.',
        'الجمعة: محتوى مجتمعي / إنساني.',
      ].join('\n'),
      applies_to: [A_UNIFIED],
      platforms: [],
      industry: null,
      service_key: null,
      priority: 60,
    },
  },
  {
    id: 'brand-compliance-claims',
    label: '٩. الالتزام بالعلامة التجارية والادعاءات',
    description: 'حدود ما يمكن قوله عن البراند والمنتج: عبارات معتمدة وأخرى ممنوعة.',
    fill: {
      scope: 'client',
      title: 'الالتزام بالعلامة التجارية',
      body: [
        'العبارات المعتمدة فقط:',
        '- [اكتب الشعار الرسمي والوصف القصير الموافَق عليه].',
        '- [اذكر الأوصاف القانونية الواجبة لمنتج العميل، مثل: "متوافق مع اشتراطات الجودة"].',
        '',
        'ممنوع:',
        '- وعود طبية أو علاجية.',
        '- ادعاءات بنتائج مالية محددة.',
        '- مقارنات مباشرة بمنافسين.',
        '- استخدام شعارات أو علامات تجارية أخرى دون إذن.',
        '',
        'يُكتب الاسم الرسمي للعميل كما هو، بدون اختصارات إلا الموافَق عليها.',
      ].join('\n'),
      applies_to: [A_UNIFIED, A_PLATFORM],
      platforms: [],
      industry: null,
      service_key: null,
      priority: 250,
    },
  },
  {
    id: 'community-reply-style',
    label: '١٠. أسلوب الردّ على التعليقات',
    description: 'صياغة الردود على المتابعين والعملاء.',
    fill: {
      scope: 'agency',
      title: 'أسلوب الردّ على التعليقات',
      body: [
        'لكل ردّ على تعليق أو رسالة خاصة:',
        '',
        '- ابدأ باسم العميل إن كان معروفًا، أو "أهلًا".',
        '- لا تتجاوز سطرين.',
        '- جاوب على السؤال أولًا، ثم وجِّه للخطوة التالية.',
        '- كل ردّ يحتوي على CTA لطيف (احجز، تواصل واتساب).',
        '- لا تستخدم ردودًا قالبية (نسخ ولصق).',
        '',
        'إن كان التعليق سلبيًا:',
        '- اعترف بالمشكلة، ثم انقل المحادثة للرسائل الخاصة.',
      ].join('\n'),
      applies_to: [A_UNIFIED],
      platforms: [],
      industry: null,
      service_key: null,
      priority: 60,
    },
  },
];
